'use client';

import React from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';
import { i18n } from '@/config/i18n';

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  min-height: 200px;
  height: calc(100vh - 60px);
  width: 100%;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.gray[200]};
  border-top-color: ${theme.colors.primary.accent};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${theme.colors.gray[600]};
  text-align: center;
`;

export interface LoadingProps {
  /** Custom loading text (defaults to i18n.common.messages.loading) */
  text?: string;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
}

/**
 * Loading Component
 * 
 * A simple loading indicator component for use in Suspense fallbacks and other loading states.
 * 
 * @component
 * @example
 * ```tsx
 * <React.Suspense fallback={<Loading />}>
 *   <MyComponent />
 * </React.Suspense>
 * 
 * <Loading text="Loading data..." />
 * ```
 */
export const Loading: React.FC<LoadingProps> = ({
  text,
  className,
  style,
}) => {
  return (
    <LoadingContainer className={className} style={style}>
      <Spinner />
      <LoadingText>{text || i18n.common.messages.loading}</LoadingText>
    </LoadingContainer>
  );
};

export default Loading;

